import { html } from 'htm/preact';
import { is } from 'bpmn-js/lib/util/ModelUtil';
import { getBreCode, isBreHandlerEntry, isBreUsage, breSortKey, navigateToElement } from './bre';
import {
  assignTrigger,
  breDescription,
  canTriggerBre,
  clearTrigger,
  createBre,
  HANDLER_NAME,
  removeBre,
  updateBre,
} from './breModeling';

function safeRoots(canvas) {
  try { return canvas.getRootElements?.() ?? []; } catch { return []; }
}

/** Collect BRE handler entries (StartEvents) and their process-side users. */
function buildEntries(elementRegistry) {
  const handlers = new Map();
  const users = new Map();

  elementRegistry.forEach((el) => {
    const code = getBreCode(el);
    if (!code) return;
    if (isBreHandlerEntry(el)) {
      if (!handlers.has(code)) {
        const bo = el.businessObject;
        handlers.set(code, {
          code,
          element: el,
          doc: bo.documentation?.[0]?.text || '',
        });
      }
    } else if (isBreUsage(el)) {
      if (!users.has(code)) users.set(code, []);
      users.get(code).push(el);
    }
  });

  return [...handlers.values()]
    .sort((a, b) => breSortKey(a.code) - breSortKey(b.code))
    .map((h) => ({ ...h, users: users.get(h.code) || [] }));
}

function hasBreHandler(elementRegistry) {
  let found = false;
  elementRegistry.forEach((el) => {
    if (found) return;
    if (is(el, 'bpmn:SubProcess') && el.businessObject?.get?.('custom:breHandler') === true) found = true;
    if (isBreHandlerEntry(el)) found = true;
  });
  return found;
}

function userLabel(el) {
  const bo = el.businessObject;
  const container = bo?.$parent?.name || bo?.$parent?.id || '';
  return container ? `${bo.name || bo.id} · ${container}` : (bo.name || bo.id);
}

/**
 * Properties-panel group "Business Rule Exceptions".
 * All services via injector (no hooks) and all canvas calls guarded, matching
 * the pattern in SubProcessTreeGroup.
 */
export default function BreListGroup(injector, element) {
  let elementRegistry, canvas;
  try {
    elementRegistry = injector.get('elementRegistry');
    canvas = injector.get('canvas');
  } catch {
    return null;
  }

  if (safeRoots(canvas).length === 0) return null;

  // The group shows up even when empty — otherwise there would be nowhere to
  // create the first exception.
  const entries = buildEntries(elementRegistry);
  const triggerElement = canTriggerBre(element) ? element : null;
  // With no exception at all it only shows on the canvas itself, so it does
  // not appear on every arbitrary element.
  const empty = entries.length === 0 && !hasBreHandler(elementRegistry);
  if (empty && !triggerElement && element && element.parent) return null;

  const BreListView = () => html`
    <div class="bre-list">
      <style>
        .bre-list ul { padding: 0; margin: 0; list-style: none; }
        .bre-entry { padding: 6px 8px; border-bottom: 1px solid #eee; }
        .bre-entry-head {
          display: flex; align-items: center; gap: 6px; width: 100%;
          border: none; background: transparent; padding: 2px 0;
          font-size: 13px; font-weight: 600; cursor: pointer; color: #0D4372; text-align: left;
        }
        .bre-entry-head:hover { text-decoration: underline; }
        .bre-code { background: #BBDEFB; color: #0D4372; border-radius: 3px; padding: 0 5px; font-size: 12px; }
        .bre-doc { font-size: 12px; color: #555; margin: 3px 0 4px; white-space: pre-wrap; }
        .bre-users { display: flex; flex-wrap: wrap; gap: 4px; }
        .bre-user {
          border: 1px solid #cfd8e3; background: #f5f8fc; border-radius: 10px;
          padding: 1px 8px; font-size: 11px; cursor: pointer; color: #333;
        }
        .bre-user:hover { background: #e8f0fe; }
        .bre-users-empty { font-size: 11px; color: #999; }
        .bre-actions { display: flex; gap: 6px; padding: 8px 8px 2px; }
        .bre-btn {
          border: 1px solid #cfd8e3; background: #f5f8fc; border-radius: 4px;
          padding: 4px 10px; font-size: 12px; cursor: pointer; color: #0D4372;
        }
        .bre-btn:hover { background: #e8f0fe; }
        .bre-btn-danger { color: #9b1c1c; }
        .bre-entry-edit { display: grid; gap: 4px; margin: 4px 0; }
        .bre-entry-edit input, .bre-entry-edit textarea {
          width: 100%; box-sizing: border-box; font-size: 12px; padding: 4px 6px;
          border: 1px solid #cfd8e3; border-radius: 3px; font-family: inherit;
        }
        .bre-entry-row { display: flex; align-items: center; gap: 6px; }
        .bre-trigger { padding: 8px; border-bottom: 1px solid #eee; }
        .bre-trigger label { display: block; font-size: 12px; color: #555; margin-bottom: 4px; }
        .bre-trigger select { width: 100%; font-size: 12px; padding: 4px 6px; }
        .bre-empty { padding: 8px; font-size: 12px; color: #666; }
      </style>
      ${triggerElement && html`
        <div class="bre-trigger">
          <label for="bre-trigger-select">Löst aus</label>
          <select id="bre-trigger-select" value=${getBreCode(triggerElement) || ''}
            onChange=${(ev) => {
              const chosen = ev.target.value;
              if (chosen === '__new__') {
                const created = createBre(injector, {});
                if (created) assignTrigger(injector, triggerElement, getBreCode(created));
              } else if (chosen) {
                assignTrigger(injector, triggerElement, chosen);
              } else {
                clearTrigger(injector, triggerElement);
              }
            }}>
            <option value="">— keine —</option>
            ${entries.map((entry) => html`
              <option value=${entry.code}>
                ${entry.code}${entry.element.businessObject.name ? ` · ${entry.element.businessObject.name}` : ''}
              </option>
            `)}
            <option value="__new__">+ neue Ausnahme anlegen…</option>
          </select>
        </div>
      `}

      ${entries.length === 0 && html`
        <div class="bre-empty">
          Noch keine Ausnahme beschrieben. „+ Neue BRE" legt den Behälter
          „${HANDLER_NAME}" an und darin den ersten Eintrag.
        </div>
      `}

      <ul>
        ${entries.map((entry) => html`
          <li class="bre-entry">
            <div class="bre-entry-row">
              <button type="button" class="bre-entry-head"
                title="Zum Handler springen"
                onClick=${() => navigateToElement(canvas, entry.element)}>
                <span class="bre-code">${entry.code}</span>
                <span>${
                  // A fresh entry is named after its own code — showing both
                  // would just read "BRE1 BRE1".
                  (entry.element.businessObject.name || '') === entry.code
                    ? ''
                    : (entry.element.businessObject.name || '')
                }</span>
              </button>
              <button type="button" class="bre-btn bre-btn-danger"
                title="Ausnahme und alle Verknüpfungen entfernen"
                onClick=${() => removeBre(injector, entry.element)}>✕</button>
            </div>
            <div class="bre-entry-edit">
              <input type="text" value=${entry.element.businessObject.name || ''}
                placeholder="Bezeichnung"
                onChange=${(ev) => updateBre(injector, entry.element, { name: ev.target.value })} />
              <textarea rows="2" placeholder="Beschreibung"
                value=${breDescription(entry.element)}
                onChange=${(ev) => updateBre(injector, entry.element, { description: ev.target.value })}></textarea>
            </div>
            ${entry.users.length > 0
              ? html`<div class="bre-users">
                  ${entry.users.map((u) => html`
                    <button type="button" class="bre-user"
                      title="Zur Verwendung springen"
                      onClick=${() => navigateToElement(canvas, u)}>
                      ${userLabel(u)}
                    </button>
                  `)}
                </div>`
              : html`<div class="bre-users-empty">keine Verwendung im Prozess gefunden</div>`}
          </li>
        `)}
      </ul>

      <div class="bre-actions">
        <button type="button" class="bre-btn" onClick=${() => createBre(injector, {})}>
          + Neue BRE
        </button>
      </div>
    </div>
  `;

  return {
    id: 'breList',
    label: 'Business Rule Exceptions',
    shouldOpen: false,
    entries: [
      { id: 'breListView', component: BreListView },
    ],
  };
}
